import { ensureCurrentUser } from './data';

export const ALTERNATIVE_PAYOUT_METHODS = {
  WISE: 'wise',
  REVOLUT: 'revolut',
  PAYPAL: 'paypal',
  BANK_TRANSFER: 'bank_transfer',
};

export const ALTERNATIVE_PAYOUT_METHOD_IDS = [
  ALTERNATIVE_PAYOUT_METHODS.WISE,
  ALTERNATIVE_PAYOUT_METHODS.REVOLUT,
  ALTERNATIVE_PAYOUT_METHODS.PAYPAL,
  ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER,
];

export const ALTERNATIVE_PAYOUT_PROTECTED_DATA_KEY = 'alternativePayout';

/** Who chose the payout the user sees. Operator Stripe setup does not set this. */
export const USER_PAYOUT_CHOICE_KEY = 'userPayoutChoice';
export const USER_PAYOUT_CHOICE_STRIPE = 'stripe';
export const USER_PAYOUT_CHOICE_ALTERNATIVE = 'alternative';

export const EMPTY_BANK_ACCOUNT = {
  bankName: '',
  currency: '',
  fullName: '',
  fullAddress: '',
  sortingCode: '',
  iban: '',
  swift: '',
};

/**
 * Read saved alternative payout details from the current user.
 * @param {object} currentUser
 * @returns {object|null}
 */
export const getAlternativePayout = currentUser => {
  const user = ensureCurrentUser(currentUser);
  const data = user?.attributes?.profile?.protectedData?.[ALTERNATIVE_PAYOUT_PROTECTED_DATA_KEY];
  if (!data || typeof data !== 'object') {
    return null;
  }
  return data;
};

/**
 * The payout path the user chose. Absent when only an operator has connected Stripe.
 * @param {object} currentUser
 * @returns {'stripe'|'alternative'|null}
 */
export const getUserPayoutChoice = currentUser => {
  const user = ensureCurrentUser(currentUser);
  const choice = user?.attributes?.profile?.protectedData?.[USER_PAYOUT_CHOICE_KEY];
  if (choice === USER_PAYOUT_CHOICE_STRIPE || choice === USER_PAYOUT_CHOICE_ALTERNATIVE) {
    return choice;
  }
  return null;
};

/** True only when this user set up Stripe, not when an operator did. */
export const userChoseStripePayout = currentUser =>
  getUserPayoutChoice(currentUser) === USER_PAYOUT_CHOICE_STRIPE;

const isNonEmpty = value => typeof value === 'string' && value.trim().length > 0;

const isBankAccountComplete = account => {
  if (!account || typeof account !== 'object') {
    return false;
  }
  const hasIdentity = isNonEmpty(account.iban) || isNonEmpty(account.swift);
  return (
    isNonEmpty(account.bankName) &&
    isNonEmpty(account.currency) &&
    isNonEmpty(account.fullName) &&
    isNonEmpty(account.fullAddress) &&
    hasIdentity
  );
};

/**
 * True when the provider has saved a usable alternative payout configuration.
 * @param {object} currentUser
 * @returns {boolean}
 */
export const isAlternativePayoutConfigured = currentUser => {
  const payout = getAlternativePayout(currentUser);
  if (!payout?.country || !payout?.accountType || !payout?.method) {
    return false;
  }
  if (!ALTERNATIVE_PAYOUT_METHOD_IDS.includes(payout.method)) {
    return false;
  }
  if (payout.method === ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER) {
    const accounts = Array.isArray(payout.bankAccounts) ? payout.bankAccounts : [];
    if (accounts.length > 0) {
      return accounts.every(isBankAccountComplete);
    }
    // Legacy single-account shape
    return (
      isNonEmpty(payout.fullName) &&
      isNonEmpty(payout.fullAddress) &&
      isNonEmpty(payout.accountDetails)
    );
  }
  return isNonEmpty(payout.handle);
};

/**
 * Build protectedData payload for alternative payout save / clear.
 * @param {object|null} alternativePayout - null clears the field
 * @returns {{ protectedData: object }}
 */
export const buildAlternativePayoutProfileUpdate = alternativePayout => ({
  protectedData: {
    [ALTERNATIVE_PAYOUT_PROTECTED_DATA_KEY]: alternativePayout,
    // Saving or clearing the user's payout must not leave an operator Stripe
    // account as the thing this user sees.
    [USER_PAYOUT_CHOICE_KEY]: alternativePayout ? USER_PAYOUT_CHOICE_ALTERNATIVE : null,
  },
});

/** Record that the user, not an operator, connected Stripe. */
export const buildUserStripePayoutChoiceUpdate = () => ({
  protectedData: {
    [USER_PAYOUT_CHOICE_KEY]: USER_PAYOUT_CHOICE_STRIPE,
    [ALTERNATIVE_PAYOUT_PROTECTED_DATA_KEY]: null,
  },
});

const normalizeBankAccount = account => {
  if (!account || typeof account !== 'object') {
    return null;
  }
  return {
    bankName: account.bankName?.trim() || null,
    currency: account.currency?.trim()?.toUpperCase() || null,
    fullName: account.fullName?.trim() || null,
    fullAddress: account.fullAddress?.trim() || null,
    sortingCode: account.sortingCode?.trim() || null,
    iban: account.iban?.trim() || null,
    swift: account.swift?.trim() || null,
  };
};

/**
 * Normalize form values into the protectedData alternativePayout shape.
 * @param {object} values
 * @returns {object|null}
 */
export const alternativePayoutFromFormValues = values => {
  if (!values || values.payoutPath === 'alternativeClear') {
    return null;
  }
  const method = values.payoutMethod;
  const bankAccounts = Array.isArray(values.bankAccounts)
    ? values.bankAccounts.map(normalizeBankAccount).filter(Boolean)
    : [];

  return {
    accountType: values.accountType,
    country: values.country,
    method,
    handle: values.payoutHandle?.trim() || null,
    bankAccounts: method === ALTERNATIVE_PAYOUT_METHODS.BANK_TRANSFER ? bankAccounts : [],
    // Clear legacy single-account fields when using the new shape
    fullName: null,
    fullAddress: null,
    accountDetails: null,
  };
};

/** Prefer profileImage only — stripeAccount include breaks denormalise when absent. */
export const ALTERNATIVE_PAYOUT_UPDATE_QUERY = {
  expand: true,
  include: ['profileImage'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

/**
 * Human-readable country for operator notifications: "Argentina (AR)".
 * @param {string} countryCode
 * @param {string} [locale]
 * @returns {string}
 */
export const formatCountryForNotify = (countryCode, locale = 'en') => {
  if (!countryCode) {
    return '—';
  }
  try {
    const name = new Intl.DisplayNames([locale], { type: 'region' }).of(countryCode);
    return name ? `${name} (${countryCode})` : countryCode;
  } catch (e) {
    return countryCode;
  }
};

/**
 * Human-readable summary for operator notification emails / WhatsApp.
 * @param {object} payout
 * @param {{ locale?: string }} [options]
 * @returns {string}
 */
export const formatAlternativePayoutForEmail = (payout, options = {}) => {
  if (!payout) {
    return 'Cleared';
  }
  const locale = options.locale || 'en';
  const lines = [
    `Account type: ${payout.accountType || '—'}`,
    `Country: ${formatCountryForNotify(payout.country, locale)}`,
    `Method: ${payout.method || '—'}`,
  ];
  if (payout.handle) {
    lines.push(`Handle: ${payout.handle}`);
  }
  const accounts = Array.isArray(payout.bankAccounts) ? payout.bankAccounts : [];
  if (accounts.length) {
    accounts.forEach((account, index) => {
      lines.push(`Bank account ${index + 1}:`);
      lines.push(`  Bank name: ${account.bankName || '—'}`);
      lines.push(`  Currency: ${account.currency || '—'}`);
      lines.push(`  Full name: ${account.fullName || '—'}`);
      lines.push(`  Address: ${account.fullAddress || '—'}`);
      lines.push(`  Sorting/IFSC: ${account.sortingCode || '—'}`);
      lines.push(`  IBAN / Account number: ${account.iban || '—'}`);
      lines.push(`  SWIFT: ${account.swift || '—'}`);
    });
  } else if (payout.accountDetails) {
    lines.push(`Full name: ${payout.fullName || '—'}`);
    lines.push(`Address: ${payout.fullAddress || '—'}`);
    lines.push(`Account details: ${payout.accountDetails || '—'}`);
  }
  return lines.join('\n');
};
